const URL_REGEX = /(https?:\/\/[^\s<]+)/gi;
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", '"', "'"]);

/**
 * The URL regex is greedy and has no way to know a sentence just ended, so
 * "https://aalen.space." would otherwise link (and display) with the trailing
 * period baked into the href. Trims common trailing punctuation off the match,
 * except a closing ")" that balances an opening "(" earlier in the URL itself
 * (so wiki-style links like ".../wiki/PHP_(programming_language)" stay whole).
 */
function trimTrailingPunctuation(url: string): string {
  let end = url.length;
  while (end > 0) {
    const ch = url[end - 1];
    if (ch === ")") {
      const upTo = url.slice(0, end);
      const opens = (upTo.match(/\(/g) ?? []).length;
      const closes = (upTo.match(/\)/g) ?? []).length;
      if (closes <= opens) break;
      end--;
      continue;
    }
    if (TRAILING_PUNCTUATION.has(ch)) {
      end--;
      continue;
    }
    break;
  }
  return url.slice(0, end);
}

/**
 * Turns plain text (which may contain newlines and bare URLs) into a safe
 * DocumentFragment: URLs become <a> elements, everything else stays as
 * escaped text nodes. Never uses innerHTML on untrusted input.
 */
export function linkify(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const lines = text.split("\n");

  lines.forEach((line, lineIndex) => {
    let lastIndex = 0;
    for (const match of line.matchAll(URL_REGEX)) {
      const rawMatch = match[0];
      const url = trimTrailingPunctuation(rawMatch);
      const index = match.index ?? 0;

      if (index > lastIndex) {
        fragment.append(line.slice(lastIndex, index));
      }

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.textContent = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      fragment.append(anchor);

      const trailing = rawMatch.slice(url.length);
      if (trailing) fragment.append(trailing);

      lastIndex = index + rawMatch.length;
    }

    if (lastIndex < line.length) {
      fragment.append(line.slice(lastIndex));
    }

    if (lineIndex < lines.length - 1) {
      fragment.append(document.createElement("br"));
    }
  });

  return fragment;
}
